import React, { useEffect, useState } from 'react';
import { useAudioRecorder } from '../../participant/capture/audio/useAudioRecorder';
import { VoicePinPlayer } from './VoicePinPlayer';

const MAX_DURATION_SEC = 120;
const WARN_DURATION_SEC = 110;

type Mode = 'idle' | 'recording' | 'saved';

interface VoicePinRecorderProps {
  initialAudioBlob?: Blob;
  initialAudioUrl?: string;
  initialDuration?: number;
  onCommit: (payload: { blob: Blob; duration: number }) => void;
  onDiscard: () => void;
  onCancel: () => void;
}

export const VoicePinRecorder: React.FC<VoicePinRecorderProps> = ({
  initialAudioBlob,
  initialAudioUrl,
  initialDuration,
  onCommit,
  onDiscard,
  onCancel,
}) => {
  const hasInitialClip = Boolean(initialAudioBlob || initialAudioUrl);
  const [mode, setMode] = useState<Mode>(hasInitialClip ? 'saved' : 'idle');
  const [capturedDuration, setCapturedDuration] = useState<number | undefined>(initialDuration);
  const [capturedBlob, setCapturedBlob] = useState<Blob | undefined>(initialAudioBlob);
  const recorder = useAudioRecorder();

  // Auto-stop at max duration.
  useEffect(() => {
    if (mode === 'recording' && recorder.duration >= MAX_DURATION_SEC) {
      recorder.stopRecording();
    }
  }, [mode, recorder.duration, recorder.stopRecording]);

  // Transition Recording → Saved once a clip lands on the recorder.
  useEffect(() => {
    if (mode === 'recording' && !recorder.isRecording && recorder.clips.length > 0) {
      const clip = recorder.clips[recorder.clips.length - 1];
      onCommit({ blob: clip.blob, duration: clip.duration });
      setCapturedDuration(clip.duration);
      setCapturedBlob(clip.blob);
      setMode('saved');
    }
  }, [mode, recorder.isRecording, recorder.clips, onCommit]);

  const startRecording = async () => {
    setMode('recording');
    await recorder.startRecording();
  };

  const stopRecording = () => {
    recorder.stopRecording();
  };

  const cancelDuringRecording = () => {
    recorder.stopRecording();
    recorder.resetAll();
    onCancel(); // Parent removes the pin entirely.
  };

  const cancelIdle = () => {
    onCancel(); // Ghost pin — parent removes it.
  };

  const discardSaved = () => {
    recorder.resetAll();
    setMode('idle');
    onDiscard(); // Parent clears audioBlob/audioUrl from the pin.
  };

  const timerColor = recorder.duration >= WARN_DURATION_SEC ? '#D4A85A' : '#3D3A36';

  if (mode === 'idle') {
    return (
      <div className="voice-pin-recorder" data-mode="idle">
        <button className="voice-pin-record-btn" onClick={startRecording} aria-label="Start recording">
          <span className="voice-pin-record-dot" />
        </button>
        <div className="voice-pin-label">
          <div className="voice-pin-label-title">Tap to record</div>
          <div className="voice-pin-label-hint">up to 2:00</div>
        </div>
        <button className="voice-pin-cancel" onClick={cancelIdle} aria-label="Cancel">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
    );
  }

  if (mode === 'recording') {
    return (
      <div className="voice-pin-recorder" data-mode="recording">
        <div className="voice-pin-header" role="status" aria-live="polite">
          <span className="voice-pin-status-dot" />
          <span className="voice-pin-status-label">Recording</span>
          <span className="voice-pin-spacer" />
          <span className="voice-pin-timer" style={{ color: timerColor }}>
            {formatTime(recorder.duration)}
          </span>
        </div>
        <LiveWaveform level={recorder.audioLevel} />
        <div className="voice-pin-actions">
          <button className="voice-pin-stop" onClick={stopRecording}>Stop</button>
          <button className="voice-pin-cancel-full" onClick={cancelDuringRecording} aria-label="Discard recording">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  // saved
  return (
    <div className="voice-pin-recorder" data-mode="saved">
      <div className="voice-pin-saved-row">
        <VoicePinPlayer
          audioUrl={initialAudioUrl}
          audioBlob={capturedBlob}
          duration={capturedDuration ?? initialDuration}
          compact
        />
        <button className="voice-pin-delete" onClick={discardSaved} aria-label="Delete recording">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 6h18" />
            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6M14 11v6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

const LiveWaveform: React.FC<{ level: number }> = ({ level }) => {
  const bars = 18;
  return (
    <div className="voice-pin-waveform live">
      {Array.from({ length: bars }).map((_, i) => {
        const h = 4 + Math.round(16 * level * ((i % 4) + 1) / 4);
        return <span key={i} className="voice-pin-waveform-bar" style={{ height: h }} />;
      })}
    </div>
  );
};

